import React, { useState, useEffect } from "react";

const HeartRateZoneCalculator = () => {
  const [age, setAge] = useState(25);
  const [restingHR, setRestingHR] = useState(60);
  const [gender, setGender] = useState("male");
  const [fitnessLevel, setFitnessLevel] = useState("intermediate");
  const [method, setMethod] = useState("karvonen");
  const [result, setResult] = useState({
    maxHR: "0",
    zones: [],
    interpretation: "",
    trainingAdvice: "",
  });

  // Heart Rate Zone Calculation
  const calculateHeartRateZones = () => {
    let a = parseFloat(age);
    let rhr = parseFloat(restingHR);

    if (isNaN(a) || a <= 0 || isNaN(rhr) || rhr <= 0) return;

    // Calculate Maximum Heart Rate using multiple formulas
    let maxHR;
    if (method === "tanaka") {
      // Tanaka Formula (2001) - More accurate for older adults
      maxHR = 208 - (0.7 * a);
    } else if (method === "gulati") {
      // Gulati Formula (2010) - Specifically for women
      maxHR = 206 - (0.88 * a);
    } else {
      // Karvonen Method (default) - 220 - age
      maxHR = 220 - a;
    }

    // Calculate Heart Rate Reserve (HRR) for Karvonen method
    const hrr = maxHR - rhr;

    // Define training zones with percentages
    const zones = [
      {
        name: "Zone 1: Recovery",
        range: [0.50, 0.60],
        color: "#94a3b8",
        icon: "🧘",
        description: "Very light activity, active recovery",
        benefits: "Recovery, warm-up, cool-down",
      },
      {
        name: "Zone 2: Endurance",
        range: [0.60, 0.70],
        color: "#10b981",
        icon: "🚶",
        description: "Light to moderate activity, fat burning",
        benefits: "Base fitness, fat burning, aerobic capacity",
      },
      {
        name: "Zone 3: Tempo",
        range: [0.70, 0.80],
        color: "#3b82f6",
        icon: "🏃",
        description: "Moderate to hard activity, aerobic training",
        benefits: "Cardiovascular fitness, lactate threshold",
      },
      {
        name: "Zone 4: Threshold",
        range: [0.80, 0.90],
        color: "#f59e0b",
        icon: "💪",
        description: "Hard activity, anaerobic threshold",
        benefits: "Performance improvement, speed endurance",
      },
      {
        name: "Zone 5: Maximum",
        range: [0.90, 1.00],
        color: "#ef4444",
        icon: "🔥",
        description: "Maximum effort, sprint training",
        benefits: "Peak performance, VO2 max improvement",
      },
    ];

    // Calculate actual heart rates for each zone
    const calculatedZones = zones.map((zone) => {
      let minHR, maxHRZone;
      
      if (method === "karvonen") {
        // Karvonen Method: uses HRR
        minHR = Math.round((hrr * zone.range[0]) + rhr);
        maxHRZone = Math.round((hrr * zone.range[1]) + rhr);
      } else {
        // Percentage of Max HR method
        minHR = Math.round(maxHR * zone.range[0]);
        maxHRZone = Math.round(maxHR * zone.range[1]);
      }

      return {
        ...zone,
        minHR,
        maxHR: maxHRZone,
        range: `${minHR} - ${maxHRZone} bpm`,
      };
    });

    // Generate interpretation based on fitness level
    let interpretation = "";
    if (fitnessLevel === "beginner") {
      interpretation = "As a beginner, focus on Zones 1-2 to build your aerobic base. Spend 80% of your training time in these zones to develop cardiovascular fitness safely.";
    } else if (fitnessLevel === "intermediate") {
      interpretation = "With intermediate fitness, balance your training across Zones 2-4. Use Zone 2 for base building (60-70% of training), Zone 3 for tempo work, and Zone 4 for interval training.";
    } else {
      interpretation = "As an advanced athlete, incorporate all zones strategically. Maintain base fitness in Zone 2, develop threshold in Zones 3-4, and use Zone 5 sparingly for high-intensity intervals.";
    }

    // Training advice based on gender
    let trainingAdvice = "";
    if (gender === "male") {
      trainingAdvice = `Your maximum heart rate is approximately ${Math.round(maxHR)} bpm. Men typically have slightly higher max heart rates in younger age groups. Focus on heart rate variability and recovery between intense sessions.`;
    } else {
      trainingAdvice = `Your maximum heart rate is approximately ${Math.round(maxHR)} bpm. Women often have higher resting heart rates but excellent endurance capacity. Pay attention to hormonal cycle effects on heart rate and performance.`;
    }

    setResult({
      maxHR: `${Math.round(maxHR)} bpm`,
      zones: calculatedZones,
      interpretation,
      trainingAdvice,
    });
  };

  useEffect(() => {
  calculateHeartRateZones();
}, [age, restingHR, gender, fitnessLevel, method]);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </div>
          <h1>Heart Rate Zone Calculator</h1>
          <p>
            Calculate your personalized heart rate training zones using scientifically validated formulas. Optimize your workouts for fat burning, endurance, or peak performance.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Your Information
              </h2>

              {/* Age Input */}
              <div className="input-group">
                <label>Age (years)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🎂</span>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="calc-input"
                    placeholder="25"
                    min="1"
                    max="100"
                  />
                </div>
                <div className="quick-select-group">
                  {[20, 30, 40, 50, 60].map((val) => (
                    <button
                      key={val}
                      onClick={() => setAge(val)}
                      className="quick-select-btn primary"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Resting Heart Rate Input */}
              <div className="input-group">
                <label>Resting Heart Rate (bpm)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">💓</span>
                  <input
                    type="number"
                    value={restingHR}
                    onChange={(e) => setRestingHR(e.target.value)}
                    className="calc-input"
                    placeholder="60"
                    min="30"
                    max="120"
                  />
                </div>
                <div className="quick-select-group">
                  {[50, 60, 70, 80, 90].map((h) => (
                    <button
                      key={h}
                      onClick={() => setRestingHR(h)}
                      className="quick-select-btn success"
                    >
                      {h}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Measure your resting heart rate first thing in the morning before getting out of bed for the most accurate reading.
                </p>
              </div>

              {/* Gender */}
              <div className="input-group">
                <label>Gender</label>
                <div className="quick-select-group">
                  {["male", "female"].map((g) => (
                    <button
                      key={g}
                      onClick={() => setGender(g)}
                      className={`quick-select-btn ${gender === g ? "primary" : "secondary"}`}
                    >
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Fitness Level */}
              <div className="input-group">
                <label>Fitness Level</label>
                <div className="quick-select-group">
                  {[
                    { value: "beginner", label: "Beginner" },
                    { value: "intermediate", label: "Intermediate" },
                    { value: "advanced", label: "Advanced" },
                  ].map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setFitnessLevel(level.value)}
                      className={`quick-select-btn ${fitnessLevel === level.value ? "primary" : "secondary"}`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Calculation Method */}
              <div className="input-group">
                <label>Calculation Method</label>
                <div className="quick-select-group">
                  {[
                    { value: "karvonen", label: "Karvonen (HRR)" },
                    { value: "tanaka", label: "Tanaka (2001)" },
                    { value: "gulati", label: "Gulati (Women)" },
                  ].map((m) => (
                    <button
                      key={m.value}
                      onClick={() => setMethod(m.value)}
                      className={`quick-select-btn ${method === m.value ? "primary" : "secondary"}`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Karvonen uses heart rate reserve for accuracy. Tanaka is better for older adults. Gulati is specifically calibrated for women.
                </p>
              </div>

              <button onClick={calculateHeartRateZones} className="calculate-btn">
                Calculate Heart Rate Zones
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                Your Training Zones
              </h2>

              <div className="result-card">
                {/* Maximum Heart Rate */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Maximum Heart Rate</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.maxHR}</div>
                  <div className="result-subtext">theoretical maximum</div>
                </div>

                {/* Heart Rate Zones */}
                <div className="breakdown-section" style={{ marginTop: "1.5rem" }}>
                  <h3 className="breakdown-title">Training Zones</h3>
                  {result.zones.map((zone, index) => (
                    <div key={index} style={{ 
                      marginBottom: "1rem", 
                      padding: "1rem", 
                      backgroundColor: "#f9fafb",
                      borderRadius: "0.5rem",
                      borderLeft: `4px solid ${zone.color}`
                    }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: "1.5rem" }}>{zone.icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "600", fontSize: "0.9rem", color: "#1f2937" }}>
                            {zone.name}
                          </div>
                          <div style={{ fontSize: "1.1rem", fontWeight: "700", color: zone.color, marginTop: "0.25rem" }}>
                            {zone.range}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#6b7280", marginBottom: "0.25rem" }}>
                        {zone.description}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#059669", fontWeight: "500" }}>
                        ✓ {zone.benefits}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Training Advice */}
                <div className="breakdown-section" style={{ marginTop: "1rem" }}>
                  <h3 className="breakdown-title">Training Guidance</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46", fontWeight: "500", marginBottom: "0.75rem" }}>
                    {result.interpretation}
                  </p>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.trainingAdvice}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Heart Rate Zones</h3>
              <p>
                Heart rate training zones help you exercise at the right intensity to achieve specific fitness goals. Training in different zones produces different physiological adaptations.
                This calculator uses scientifically validated formulas including the Karvonen method (using heart rate reserve), Tanaka formula (208 - 0.7 × age), and Gulati formula (specifically for women).
                By training in specific zones, you can optimize fat burning, build endurance, improve lactate threshold, or maximize athletic performance.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Calculation Methods:</h4>
            <div className="formula-display">
              Karvonen Method: Target HR = ((Max HR - Resting HR) × %Intensity) + Resting HR
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Tanaka Formula: Max HR = 208 - (0.7 × Age) - More accurate for older adults
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Gulati Formula: Max HR = 206 - (0.88 × Age) - Specifically for women
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Standard Formula: Max HR = 220 - Age - Traditional method
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Zone 1</div>
                <div className="formula-param-value">50-60% intensity</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Zone 2</div>
                <div className="formula-param-value">60-70% intensity</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Zone 3</div>
                <div className="formula-param-value">70-80% intensity</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Zone 4</div>
                <div className="formula-param-value">80-90% intensity</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Zone 5</div>
                <div className="formula-param-value">90-100% intensity</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How do I measure my resting heart rate?</h5>
              <p>
                Measure your pulse for 60 seconds immediately upon waking, before getting out of bed. Do this for 3-5 consecutive days and take the average for the most accurate reading.
              </p>
            </div>

            <div className="faq-item">
              <h5>Which heart rate zone is best for fat burning?</h5>
              <p>
                Zone 2 (60-70% intensity) is often called the "fat-burning zone" because your body uses fat as the primary fuel source. However, higher intensity zones burn more total calories.
              </p>
            </div>

            <div className="faq-item">
              <h5>How accurate are these formulas?</h5>
              <p>
                These formulas provide good estimates for most people but can vary by ±10-15 bpm. For precise zones, consider a lab-based VO2 max test or lactate threshold testing.
              </p>
            </div>

            <div className="faq-item">
              <h5>Can I train in Zone 5 every day?</h5>
              <p>
                No. Zone 5 training is extremely demanding and should be limited to 1-2 sessions per week with adequate recovery. Most training should be in Zones 2-3 for sustainable progress.
              </p>
            </div>

            <div className="faq-item">
              <h5>Do medications affect heart rate zones?</h5>
              <p>
                Yes. Beta-blockers and other medications can significantly lower heart rate. If you take heart medications, consult your doctor before using heart rate zones for training.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the difference between Karvonen and percentage methods?</h5>
              <p>
                The Karvonen method uses heart rate reserve (max HR minus resting HR), which provides more personalized zones based on your fitness level. It's generally more accurate than simple percentage of max HR.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeartRateZoneCalculator;